import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Search, ChevronDown, ChevronRight, Pencil } from "lucide-react";
import { toast } from "sonner";
import { FUNCTION_COLORS } from "@/lib/constants";

export default function FrameworkLibrary() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [frameworks, setFrameworks] = useState([]);
  const [selectedFramework, setSelectedFramework] = useState("NIST_CSF_2_0");
  const [tree, setTree] = useState([]);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadFrameworks = async () => {
    const { data } = await api.get("/framework/frameworks");
    setFrameworks(data);
    if (data?.length && !data.find((f) => f.code === selectedFramework)) {
      setSelectedFramework(data[0].code);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/framework/tree?framework_code=${encodeURIComponent(selectedFramework)}`);
      setTree(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadFrameworks(); }, []);
  useEffect(() => { load(); }, [selectedFramework]);

  const current = frameworks.find((f) => f.code === selectedFramework);
  const totalControls = tree.reduce((acc, f) => acc + f.categories.reduce((s, c) => s + c.subcategories.length, 0), 0);

  const matches = (s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [
      s.subcategory_code,
      s.description,
      s.category_name,
      s.function_name,
      s.framework_name,
    ].some((x) => x?.toLowerCase().includes(q));
  };

  return (
    <div data-testid="framework-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-uppercase">Unified Framework Library</div>
          <h1 className="text-3xl tracking-tight font-medium mt-2 text-white">
            {current ? `${current.name} ${current.version || ""}` : "Framework Library"}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            NIST CSF 2.0 · ISO 27001:2022 · SOC 2
          </p>
        </div>
        <div className="text-xs text-zinc-500 mono">{totalControls} controls</div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {frameworks.map((fw) => (
          <button
            key={fw.code}
            onClick={() => setSelectedFramework(fw.code)}
            className={`px-3 py-2 rounded-md border text-xs transition ${
              selectedFramework === fw.code
                ? "bg-white text-black border-white"
                : "bg-[#121212] text-zinc-400 border-zinc-800 hover:text-white"
            }`}
            data-testid={`framework-tab-${fw.code}`}
          >
            {fw.name} {fw.version}
          </button>
        ))}
      </div>

      <div className="mt-6 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search controls…"
          data-testid="framework-search"
          className="pl-10 bg-[#121212] border-zinc-800"
        />
      </div>

      {loading ? (
        <div className="mt-6 text-zinc-500 text-sm">Loading framework…</div>
      ) : (
        <div className="mt-4 space-y-3">
          {tree.map((f) => {
            const fOpen = expanded[`f-${f.code}`] ?? true;
            const visibleCats = f.categories
              .map((c) => ({ ...c, subcategories: c.subcategories.filter(matches) }))
              .filter((c) => c.subcategories.length > 0);
            if (search && visibleCats.length === 0) return null;
            return (
              <div key={f.code} className="rounded-md border border-zinc-800 bg-[#121212] overflow-hidden" data-testid={`fn-block-${f.code}`}>
                <button
                  onClick={() => setExpanded((s) => ({ ...s, [`f-${f.code}`]: !fOpen }))}
                  className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-zinc-900/40"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full" style={{ background: FUNCTION_COLORS[f.code] || "#A1A1AA" }} />
                    {fOpen ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
                    <span className="text-base mono text-white font-medium">{f.code}</span>
                    <span className="text-sm text-zinc-300">{f.name}</span>
                  </div>
                  <span className="text-[10px] text-zinc-500 mono">
                    {visibleCats.reduce((s, c) => s + c.subcategories.length, 0)} controls
                  </span>
                </button>
                {fOpen && (
                  <div className="border-t border-zinc-800 divide-y divide-zinc-800">
                    {(search ? visibleCats : f.categories).map((c) => (
                      <CategoryBlock key={c.code} category={c} isAdmin={isAdmin} onEdit={setEditing} matches={matches} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <SubcategoryEditDialog item={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}

function CategoryBlock({ category, isAdmin, onEdit, matches }) {
  const [open, setOpen] = useState(true);
  const subs = category.subcategories.filter(matches);
  return (
    <div data-testid={`cat-block-${category.code}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left hover:bg-zinc-900/40">
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronRight className="w-3.5 h-3.5 text-zinc-500" />}
          <span className="text-xs mono text-white font-medium">{category.code}</span>
          <span className="text-xs text-zinc-300">{category.name}</span>
        </div>
        <span className="text-[10px] text-zinc-500">{subs.length} controls</span>
      </button>
      {open && (
        <div className="border-t border-zinc-800 divide-y divide-zinc-800">
          {subs.map((sub) => (
            <div key={sub.id} className="px-5 py-3 hover:bg-zinc-900/30 flex items-start justify-between gap-3" data-testid={`sub-${sub.subcategory_code}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs mono text-white font-medium">{sub.subcategory_code}</span>
                  {sub.critical && <Badge className="bg-red-500/10 text-red-400 border-red-500/30 text-[10px] py-0">CRITICAL</Badge>}
                  <Badge className="bg-zinc-900 text-zinc-400 border-zinc-800 text-[10px] py-0 mono">w {Number(sub.default_weight || 1).toFixed(1)}</Badge>
                </div>
                <div className="text-[12px] text-zinc-400 mt-1 leading-relaxed">{sub.description}</div>
              </div>
              {isAdmin && (
                <button onClick={() => onEdit(sub)} className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-white" data-testid={`edit-sub-${sub.subcategory_code}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SubcategoryEditDialog({ item, onClose, onSaved }) {
  const [form, setForm] = useState({ default_weight: 1, critical: false, scoring_guidance: "", recommended_evidence: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (item) setForm({
      default_weight: item.default_weight || 1,
      critical: !!item.critical,
      scoring_guidance: item.scoring_guidance || "",
      recommended_evidence: item.recommended_evidence || "",
    });
  }, [item]);

  if (!item) return null;

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/framework/subcategories/${item.id}`, form);
      toast.success("Updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white sm:max-w-lg" data-testid="framework-edit-dialog">
        <DialogHeader><DialogTitle>Edit {item.subcategory_code}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-zinc-400 leading-relaxed">{item.description}</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-uppercase">Default Weight</Label>
              <Input type="number" step="0.1" min="0" max="5" value={form.default_weight}
                onChange={(e) => setForm({ ...form, default_weight: Number(e.target.value) })}
                data-testid="weight-input" className="mt-1.5 bg-[#0A0A0A] border-zinc-800" />
            </div>
            <div>
              <Label className="label-uppercase">Critical</Label>
              <div className="mt-3 flex items-center gap-2">
                <Switch checked={form.critical} onCheckedChange={(v) => setForm({ ...form, critical: v })} data-testid="critical-switch" />
                <span className="text-xs text-zinc-400">{form.critical ? "Critical control" : "Standard"}</span>
              </div>
            </div>
          </div>
          <div>
            <Label className="label-uppercase">Scoring Guidance</Label>
            <Textarea rows={3} value={form.scoring_guidance} onChange={(e) => setForm({ ...form, scoring_guidance: e.target.value })}
              data-testid="guidance-input" className="mt-1.5 bg-[#0A0A0A] border-zinc-800" />
          </div>
          <div>
            <Label className="label-uppercase">Recommended Evidence</Label>
            <Textarea rows={3} value={form.recommended_evidence} onChange={(e) => setForm({ ...form, recommended_evidence: e.target.value })}
              data-testid="rec-evidence-input" className="mt-1.5 bg-[#0A0A0A] border-zinc-800" />
          </div>
        </div>
        <DialogFooter className="gap-2 pt-3">
          <Button variant="outline" onClick={onClose} className="border-zinc-800 text-zinc-300 hover:bg-zinc-900">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-white text-black hover:bg-zinc-200" data-testid="framework-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
